// src/components/layout/NotificationBell.tsx
import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Bell, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { apiService } from '../../services/api'

interface NotifItem {
  id: string
  title: string
  message: string
  priority: string
  frequency: string
  createdAt: string
  isRead: boolean
}

const PRIORITY: Record<string, { bg: string; border: string; dot: string }> = {
  info: { bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-500' },
  warning: { bg: 'bg-orange-50', border: 'border-orange-200', dot: 'bg-orange-500' },
  important: { bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500' },
}

export default function NotificationBell() {
  const [items, setItems] = useState<NotifItem[]>([])
  const [unread, setUnread] = useState(0)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const fetchNotifs = useCallback(async () => {
    try {
      const res = await apiService.getNotifications()
      setItems(res.items || [])
      setUnread(res.unreadCount || 0)
    } catch { /* sessiz */ }
  }, [])

  // Canlı: ilk yükle + her 20 sn'de bir kontrol (sayfa yenilemeye gerek yok)
  useEffect(() => {
    fetchNotifs()
    const id = setInterval(fetchNotifs, 20000)
    return () => clearInterval(id)
  }, [fetchNotifs])

  // Panel dışına tıklayınca kapat
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  const togglePanel = async () => {
    const next = !open
    setOpen(next)
    if (next && unread > 0) {
      try {
        await apiService.markNotificationsRead()
        setUnread(0)
        setItems(prev => prev.map(i => ({ ...i, isRead: true })))
      } catch { /* sessiz */ }
    }
  }

  const fmt = (d: string) =>
    new Date(d).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={togglePanel}
        className="relative border border-orange-300 text-orange-600 hover:bg-orange-50 rounded-md px-4 py-2 flex items-center text-sm font-medium transition-colors"
      >
        <Bell className="w-4 h-4 mr-2" />
        Bildirimler
        {unread > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-h-[28rem] bg-white rounded-xl shadow-xl border border-gray-200 z-50 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="font-bold text-gray-800">Sistem Mesajları</h3>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="overflow-y-auto p-2">
            {items.length === 0 ? (
              <p className="text-center text-gray-500 text-sm py-8">Henüz mesaj yok.</p>
            ) : (
              items.slice(0, 3).map(n => {
                const p = PRIORITY[n.priority] || PRIORITY.info
                return (
                  <div key={n.id} className={`rounded-lg border ${p.border} ${p.bg} p-3 mb-2`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-2 h-2 rounded-full ${p.dot} shrink-0`} />
                      <span className="font-semibold text-gray-800 text-sm">{n.title}</span>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap break-words line-clamp-3">{n.message}</p>
                    <p className="text-xs text-gray-400 mt-1">{fmt(n.createdAt)}</p>
                  </div>
                )
              })
            )}
          </div>
          {items.length > 0 && (
            <button
              onClick={() => { setOpen(false); navigate('/notifications') }}
              className="border-t border-gray-100 px-4 py-3 text-sm text-orange-600 hover:bg-orange-50 font-medium text-center"
            >
              Hepsini Görüntüle
            </button>
          )}
        </div>
      )}
    </div>
  )
}
